import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from '../../../store/hooks.ts';
import {
  selectDoraRisks,
  setDoraRisks,
} from '../store/risk/riskSlice.ts';
import {
  selectDoraIncidents,
  setDoraIncidents,
} from '../store/incident/incidentSlice.ts';
import {
  selectDoraTestingExercises,
  setDoraTestingExercises,
} from '../store/testingExercise/testingExerciseSlice.ts';
import {
  selectDoraControls,
  setDoraControls,
} from '../store/control/controlSlice.ts';
import {
  addDoraFinding,
  removeDoraFinding,
  selectDoraFindings,
  setDoraFindings,
  updateDoraFinding as updateDoraFindingState,
} from '../store/finding/findingSlice.ts';
import {
  listDoraRisks,
  listDoraIncidents,
  listDoraTestingExercises,
  listDoraControls,
  listDoraFindings,
  createDoraFinding,
  updateDoraFinding,
  deleteDoraFinding,
} from '../../../api.ts';
import { DoraFinding, doraFindingToUpdate } from '../models/doraFinding.ts';
import { selectCurrentOrganization } from '../../../store/organization/organizationSlice.ts';

const FINDING_STATUSES = ['open', 'resolved', 'accepted'];

export const FindingsPanel: React.FC = () => {
  const dispatch = useAppDispatch();
  const risks = useAppSelector(selectDoraRisks);
  const incidents = useAppSelector(selectDoraIncidents);
  const exercises = useAppSelector(selectDoraTestingExercises);
  const controls = useAppSelector(selectDoraControls);
  const findings = useAppSelector(selectDoraFindings);
  const currentOrganization = useAppSelector(selectCurrentOrganization);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<string>('open');
  const [riskId, setRiskId] = useState<number | ''>('');
  const [incidentId, setIncidentId] = useState<number | ''>('');
  const [exerciseId, setExerciseId] = useState<number | ''>('');
  const [controlId, setControlId] = useState<number | ''>('');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStatus, setEditStatus] = useState<string>('open');
  const [editRiskId, setEditRiskId] = useState<number | ''>('');
  const [editIncidentId, setEditIncidentId] = useState<number | ''>('');
  const [editExerciseId, setEditExerciseId] = useState<number | ''>('');
  const [editControlId, setEditControlId] = useState<number | ''>('');

  const sortedFindings = useMemo(
    () => [...findings].sort((a, b) => a.title.localeCompare(b.title)),
    [findings],
  );

  const createDisabled = !title.trim();

  useEffect(() => {
    const load = async () => {
      if (!currentOrganization) return;
      setLoading(true);
      setError(null);
      try {
        const [riskList, incidentList, exerciseList, controlList, findingList] = await Promise.all([
          listDoraRisks(),
          listDoraIncidents(),
          listDoraTestingExercises(),
          listDoraControls(),
          listDoraFindings(),
        ]);
        dispatch(setDoraRisks(riskList));
        dispatch(setDoraIncidents(incidentList));
        dispatch(setDoraTestingExercises(exerciseList));
        dispatch(setDoraControls(controlList));
        dispatch(setDoraFindings(findingList));
      } catch (err: any) {
        console.error('Failed to load findings', err);
        setError(err?.response?.data?.detail || 'Failed to load findings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentOrganization, dispatch]);

  const handleCreate = async () => {
    if (!title.trim()) {
      setError('Title is required');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const created = await createDoraFinding({
        title: title.trim(),
        description: description || null,
        status,
        created_at: new Date().toISOString(),
        risk_id: riskId === '' ? null : Number(riskId),
        incident_id: incidentId === '' ? null : Number(incidentId),
        testing_exercise_id: exerciseId === '' ? null : Number(exerciseId),
        control_id: controlId === '' ? null : Number(controlId),
      });
      dispatch(addDoraFinding(created));
      setTitle('');
      setDescription('');
      setStatus('open');
      setRiskId('');
      setIncidentId('');
      setExerciseId('');
      setControlId('');
      setSuccess('Finding created');
    } catch (err: any) {
      console.error('Failed to create finding', err);
      setError(err?.response?.data?.detail || 'Failed to create finding');
    } finally {
      setLoading(false);
    }
  };

  const startEdit = (finding: DoraFinding) => {
    setEditingId(finding.id ?? null);
    setEditTitle(finding.title);
    setEditDescription(finding.description || '');
    setEditStatus(finding.status);
    setEditRiskId(finding.risk_id ?? '');
    setEditIncidentId(finding.incident_id ?? '');
    setEditExerciseId(finding.testing_exercise_id ?? '');
    setEditControlId(finding.control_id ?? '');
    setError(null);
    setSuccess(null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditTitle('');
    setEditDescription('');
    setEditStatus('open');
    setEditRiskId('');
    setEditIncidentId('');
    setEditExerciseId('');
    setEditControlId('');
  };

  const handleUpdate = async (finding: DoraFinding) => {
    if (!finding.id) return;
    if (!editTitle.trim()) {
      setError('Title is required');
      return;
    }
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const payload = doraFindingToUpdate({
        ...finding,
        title: editTitle.trim(),
        description: editDescription || null,
        status: editStatus,
        risk_id: editRiskId === '' ? null : Number(editRiskId),
        incident_id: editIncidentId === '' ? null : Number(editIncidentId),
        testing_exercise_id: editExerciseId === '' ? null : Number(editExerciseId),
        control_id: editControlId === '' ? null : Number(editControlId),
      });
      const updated = await updateDoraFinding(finding.id, payload);
      dispatch(updateDoraFindingState(updated));
      cancelEdit();
      setSuccess('Finding updated');
    } catch (err: any) {
      console.error('Failed to update finding', err);
      setError(err?.response?.data?.detail || 'Failed to update finding');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id?: number) => {
    if (!id) return;
    if (!window.confirm('Delete this finding?')) return;
    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      await deleteDoraFinding(id);
      dispatch(removeDoraFinding(id));
      setSuccess('Finding deleted');
    } catch (err: any) {
      console.error('Failed to delete finding', err);
      setError(err?.response?.data?.detail || 'Failed to delete finding');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Add Finding
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="finding-status-label">Status</InputLabel>
              <Select
                labelId="finding-status-label"
                value={status}
                label="Status"
                onChange={(e: SelectChangeEvent<string>) => setStatus(e.target.value)}
              >
                {FINDING_STATUSES.map(option => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel id="finding-risk-label" shrink>Risk</InputLabel>
              <Select
                labelId="finding-risk-label"
                value={riskId === '' ? '' : String(riskId)}
                label="Risk"
                onChange={(e: SelectChangeEvent<string>) => setRiskId(e.target.value ? Number(e.target.value) : '')}
                displayEmpty
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {risks.map(risk => (
                  <MenuItem key={risk.id} value={risk.id}>{risk.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="finding-incident-label" shrink>Incident</InputLabel>
              <Select
                labelId="finding-incident-label"
                value={incidentId === '' ? '' : String(incidentId)}
                label="Incident"
                onChange={(e: SelectChangeEvent<string>) => setIncidentId(e.target.value ? Number(e.target.value) : '')}
                displayEmpty
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {incidents.map(incident => (
                  <MenuItem key={incident.id} value={incident.id}>{incident.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="finding-exercise-label" shrink>Testing Exercise</InputLabel>
              <Select
                labelId="finding-exercise-label"
                value={exerciseId === '' ? '' : String(exerciseId)}
                label="Testing Exercise"
                onChange={(e: SelectChangeEvent<string>) => setExerciseId(e.target.value ? Number(e.target.value) : '')}
                displayEmpty
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {exercises.map(exercise => (
                  <MenuItem key={exercise.id} value={exercise.id}>{exercise.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="finding-control-label" shrink>Control</InputLabel>
              <Select
                labelId="finding-control-label"
                value={controlId === '' ? '' : String(controlId)}
                label="Control"
                onChange={(e: SelectChangeEvent<string>) => setControlId(e.target.value ? Number(e.target.value) : '')}
                displayEmpty
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {controls.map(control => (
                  <MenuItem key={control.id} value={control.id}>{control.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button variant="contained" onClick={handleCreate} disabled={loading || createDisabled}>
              Create
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}

      {(risks.length === 0 || incidents.length === 0 || exercises.length === 0 || controls.length === 0) && (
        <Alert severity="info">
          Missing optional data for linking findings:{' '}
          {risks.length === 0 && <><strong>Risks</strong> (Risks panel), </>}
          {incidents.length === 0 && <><strong>Incidents</strong> (Incidents panel), </>}
          {exercises.length === 0 && <><strong>Testing Exercises</strong> (Testing Exercises panel), </>}
          {controls.length === 0 && <><strong>Controls</strong> (Controls panel)</>}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
            <Typography variant="h6">Findings</Typography>
            {loading && <CircularProgress size={20} />}
          </Stack>
          {sortedFindings.length === 0 ? (
            <Alert severity="info">No findings yet. Add one above.</Alert>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Risk</TableCell>
                    <TableCell>Incident</TableCell>
                    <TableCell>Testing Exercise</TableCell>
                    <TableCell>Control</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedFindings.map(finding => {
                    const isEditing = editingId === finding.id;
                    const riskTitle = finding.risk_id
                      ? (risks.find(r => r.id === finding.risk_id)?.title || '—')
                      : 'None';
                    const incidentTitle = finding.incident_id
                      ? (incidents.find(i => i.id === finding.incident_id)?.title || '—')
                      : 'None';
                    const exerciseName = finding.testing_exercise_id
                      ? (exercises.find(e => e.id === finding.testing_exercise_id)?.name || '—')
                      : 'None';
                    const controlName = finding.control_id
                      ? (controls.find(c => c.id === finding.control_id)?.name || '—')
                      : 'None';
                    return (
                      <TableRow key={finding.id ?? finding.title}>
                        <TableCell width="20%">
                          {isEditing ? (
                            <TextField value={editTitle} onChange={(e) => setEditTitle(e.target.value)} size="small" fullWidth />
                          ) : finding.title}
                        </TableCell>
                        <TableCell width="12%">
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editStatus}
                                onChange={(e: SelectChangeEvent<string>) => setEditStatus(e.target.value)}
                              >
                                {FINDING_STATUSES.map(option => (
                                  <MenuItem key={option} value={option}>{option}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : (
                            <Chip label={finding.status} size="small" color={finding.status === 'open' ? 'warning' : finding.status === 'resolved' ? 'success' : 'default'} />
                          )}
                        </TableCell>
                        <TableCell width="16%">
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editRiskId === '' ? '' : String(editRiskId)}
                                onChange={(e: SelectChangeEvent<string>) => setEditRiskId(e.target.value ? Number(e.target.value) : '')}
                                displayEmpty
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {risks.map(risk => (
                                  <MenuItem key={risk.id} value={risk.id}>{risk.title}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : riskTitle}
                        </TableCell>
                        <TableCell width="16%">
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editIncidentId === '' ? '' : String(editIncidentId)}
                                onChange={(e: SelectChangeEvent<string>) => setEditIncidentId(e.target.value ? Number(e.target.value) : '')}
                                displayEmpty
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {incidents.map(incident => (
                                  <MenuItem key={incident.id} value={incident.id}>{incident.title}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : incidentTitle}
                        </TableCell>
                        <TableCell width="16%">
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editExerciseId === '' ? '' : String(editExerciseId)}
                                onChange={(e: SelectChangeEvent<string>) => setEditExerciseId(e.target.value ? Number(e.target.value) : '')}
                                displayEmpty
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {exercises.map(exercise => (
                                  <MenuItem key={exercise.id} value={exercise.id}>{exercise.name}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : exerciseName}
                        </TableCell>
                        <TableCell width="16%">
                          {isEditing ? (
                            <FormControl fullWidth size="small">
                              <Select
                                value={editControlId === '' ? '' : String(editControlId)}
                                onChange={(e: SelectChangeEvent<string>) => setEditControlId(e.target.value ? Number(e.target.value) : '')}
                                displayEmpty
                              >
                                <MenuItem value="">
                                  <em>None</em>
                                </MenuItem>
                                {controls.map(control => (
                                  <MenuItem key={control.id} value={control.id}>{control.name}</MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          ) : controlName}
                        </TableCell>
                        <TableCell align="right">
                          {isEditing ? (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton onClick={() => handleUpdate(finding)} size="small" disabled={loading}>
                                <SaveIcon fontSize="small" />
                              </IconButton>
                              <IconButton onClick={cancelEdit} size="small" disabled={loading}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ) : (
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <IconButton onClick={() => startEdit(finding)} size="small" disabled={loading}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton onClick={() => handleDelete(finding.id)} size="small" disabled={loading}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Stack>
  );
};

export default FindingsPanel;
